import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Button,
  Flex,
  Text,
  TextField,
  Dialog,
  DialogContentContainer,
  IconButton,
  Avatar,
  Divider,
} from '@vibe/core';
import {
  Close,
  Delete,
  Person,
  Calendar,
  MoveArrowRight,
} from '@vibe/icons';
import { todayISO } from '../../utils/dates';
import '../../styles/multiselect.css';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface MultiSelectToolbarProps {
  /** IDs of the currently selected schedule items */
  selectedItemIds: string[];
  /** Available assignees for the "Move to Assignee" picker */
  assigneeOptions: Array<{ id: number; name: string }>;
  /** Remove selected items from the schedule (bulk unplan) */
  onUnplan: (itemIds: string[]) => void;
  /** Reassign selected items to a different user */
  onReassign: (itemIds: string[], targetUserId: number) => void;
  /** Move all selected items so they start on a specific date (preserving duration) */
  onMoveToDate: (itemIds: string[], targetDate: string) => void;
  /** Shift all selected items forward or backward by N days */
  onShiftDuration: (itemIds: string[], daysDelta: number) => void;
  /** Clear the current selection */
  onCancelSelection: () => void;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * Inline dropdown for choosing an assignee from the list.
 */
const AssigneePickerContent: React.FC<{
  options: Array<{ id: number; name: string }>;
  onSelect: (userId: number) => void;
}> = ({ options, onSelect }) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const lower = search.toLowerCase();
    return options.filter((o) => o.name.toLowerCase().includes(lower));
  }, [options, search]);

  return (
    <DialogContentContainer className="rp-ms-toolbar__dialog-content">
      <div className="rp-ms-toolbar__dialog-header">
        <Text type={Text.types.TEXT2} weight={Text.weights.MEDIUM}>
          Reassign to
        </Text>
      </div>
      <TextField
        size={TextField.sizes.SMALL}
        placeholder="Search people..."
        value={search}
        onChange={(value: string) => setSearch(value)}
        className="rp-ms-toolbar__search-input"
      />
      <div className="rp-ms-toolbar__assignee-list">
        {filtered.length === 0 ? (
          <Text
            type={Text.types.TEXT2}
            className="rp-ms-toolbar__empty-state"
          >
            No matching people found
          </Text>
        ) : (
          filtered.map((person) => (
            <button
              key={person.id}
              type="button"
              className="rp-ms-toolbar__assignee-option"
              onClick={() => onSelect(person.id)}
            >
              <Avatar
                type="text"
                text={person.name}
                size="small"
                ariaLabel={person.name}
              />
              <Text type={Text.types.TEXT2}>{person.name}</Text>
            </button>
          ))
        )}
      </div>
    </DialogContentContainer>
  );
};

/**
 * Inline dropdown for picking a target date.
 */
const DatePickerContent: React.FC<{
  onSelect: (date: string) => void;
}> = ({ onSelect }) => {
  const [date, setDate] = useState(todayISO());

  const handleApply = useCallback(() => {
    if (date) {
      onSelect(date);
    }
  }, [date, onSelect]);

  return (
    <DialogContentContainer className="rp-ms-toolbar__dialog-content">
      <div className="rp-ms-toolbar__dialog-header">
        <Text type={Text.types.TEXT2} weight={Text.weights.MEDIUM}>
          Move start date to
        </Text>
      </div>
      <TextField
        type={TextField.types.DATE}
        size={TextField.sizes.SMALL}
        value={date}
        onChange={(value: string) => setDate(value)}
      />
      <Button
        size={Button.sizes.SMALL}
        onClick={handleApply}
        disabled={!date}
        className="rp-ms-toolbar__dialog-apply"
      >
        Apply
      </Button>
    </DialogContentContainer>
  );
};

/**
 * Inline dropdown for shifting items by a number of days.
 */
const ShiftDurationContent: React.FC<{
  onSelect: (daysDelta: number) => void;
}> = ({ onSelect }) => {
  const [value, setValue] = useState('0');
  const [error, setError] = useState<string | null>(null);

  const handleApply = useCallback(() => {
    const parsed = parseInt(value, 10);
    if (isNaN(parsed)) {
      setError('Enter a valid number');
      return;
    }
    if (parsed === 0) {
      setError('Shift must be non-zero');
      return;
    }
    setError(null);
    onSelect(parsed);
  }, [value, onSelect]);

  return (
    <DialogContentContainer className="rp-ms-toolbar__dialog-content">
      <div className="rp-ms-toolbar__dialog-header">
        <Text type={Text.types.TEXT2} weight={Text.weights.MEDIUM}>
          Shift by days
        </Text>
      </div>
      <Text type={Text.types.TEXT2} className="rp-ms-toolbar__hint">
        Positive values move forward, negative values move backward.
      </Text>
      <TextField
        type={TextField.types.NUMBER}
        size={TextField.sizes.SMALL}
        value={value}
        onChange={(v: string) => {
          setValue(v);
          setError(null);
        }}
        placeholder="e.g. 7 or -3"
      />
      {error && (
        <Text
          type={Text.types.TEXT2}
          className="rp-ms-toolbar__error"
        >
          {error}
        </Text>
      )}
      <Button
        size={Button.sizes.SMALL}
        onClick={handleApply}
        className="rp-ms-toolbar__dialog-apply"
      >
        Apply
      </Button>
    </DialogContentContainer>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * Sticky bottom toolbar that appears when one or more schedule items are
 * selected in multi-select mode.
 *
 * Provides bulk operations:
 * - Unplan: remove items from the schedule (FR-MS-02)
 * - Move to Assignee: reassign all selected to one person (FR-MS-03)
 * - Move Date: shift all items to start on a specific date (FR-MS-04)
 * - Shift Duration: move items forward/backward by N days (FR-MS-05)
 * - Cancel: clear the selection (FR-MS-01)
 */
const MultiSelectToolbar: React.FC<MultiSelectToolbarProps> = ({
  selectedItemIds,
  assigneeOptions,
  onUnplan,
  onReassign,
  onMoveToDate,
  onShiftDuration,
  onCancelSelection,
}) => {
  // Refs for Dialog anchoring
  const assigneeBtnRef = useRef<HTMLDivElement>(null);
  const dateBtnRef = useRef<HTMLDivElement>(null);
  const shiftBtnRef = useRef<HTMLDivElement>(null);

  // Dialog open states
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [shiftOpen, setShiftOpen] = useState(false);

  // Confirmation state for unplan (destructive action)
  const [confirmUnplan, setConfirmUnplan] = useState(false);

  const count = selectedItemIds.length;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleUnplan = useCallback(() => {
    onUnplan(selectedItemIds);
    setConfirmUnplan(false);
  }, [selectedItemIds, onUnplan]);

  const handleReassign = useCallback(
    (targetUserId: number) => {
      onReassign(selectedItemIds, targetUserId);
      setAssigneeOpen(false);
    },
    [selectedItemIds, onReassign],
  );

  const handleMoveToDate = useCallback(
    (targetDate: string) => {
      onMoveToDate(selectedItemIds, targetDate);
      setDateOpen(false);
    },
    [selectedItemIds, onMoveToDate],
  );

  const handleShift = useCallback(
    (daysDelta: number) => {
      onShiftDuration(selectedItemIds, daysDelta);
      setShiftOpen(false);
    },
    [selectedItemIds, onShiftDuration],
  );

  // Don't render if nothing is selected
  if (count === 0) {
    return null;
  }

  return (
    <div
      className="rp-ms-toolbar"
      role="toolbar"
      aria-label="Multi-select actions"
    >
      <Flex
        align={Flex.align.CENTER}
        gap={Flex.gaps.MEDIUM}
        className="rp-ms-toolbar__inner"
      >
        {/* ── Selection count ──────────────────────────────────────────── */}
        <Text
          type={Text.types.TEXT1}
          weight={Text.weights.MEDIUM}
          className="rp-ms-toolbar__count"
        >
          {count} {count === 1 ? 'item' : 'items'} selected
        </Text>

        <Divider direction={Divider.directions.VERTICAL} />

        {/* ── Unplan ───────────────────────────────────────────────────── */}
        <div ref={assigneeBtnRef}>
          <Dialog
            content={
              <DialogContentContainer className="rp-ms-toolbar__dialog-content">
                <Text type={Text.types.TEXT2}>
                  Remove {count} {count === 1 ? 'item' : 'items'} from the
                  schedule? The monday.com items will not be deleted.
                </Text>
                <Flex
                  gap={Flex.gaps.SMALL}
                  justify={Flex.justify.END}
                  className="rp-ms-toolbar__confirm-actions"
                >
                  <Button
                    size={Button.sizes.SMALL}
                    kind={Button.kinds.TERTIARY}
                    onClick={() => setConfirmUnplan(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size={Button.sizes.SMALL}
                    color={Button.colors.NEGATIVE}
                    onClick={handleUnplan}
                  >
                    Unplan
                  </Button>
                </Flex>
              </DialogContentContainer>
            }
            hideTrigger={['clickoutside', 'esckey']}
            showTrigger={[]}
            open={confirmUnplan}
            position="top"
          >
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.TERTIARY}
              leftIcon={Delete}
              onClick={() => setConfirmUnplan(true)}
            >
              Unplan
            </Button>
          </Dialog>
        </div>

        {/* ── Reassign ─────────────────────────────────────────────────── */}
        <Dialog
          content={
            <AssigneePickerContent
              options={assigneeOptions}
              onSelect={handleReassign}
            />
          }
          hideTrigger={['clickoutside', 'esckey']}
          showTrigger={[]}
          open={assigneeOpen}
          position="top"
        >
          <div ref={dateBtnRef}>
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.TERTIARY}
              leftIcon={Person}
              onClick={() => setAssigneeOpen((prev) => !prev)}
            >
              Move to Assignee
            </Button>
          </div>
        </Dialog>

        {/* ── Move to date ─────────────────────────────────────────────── */}
        <Dialog
          content={<DatePickerContent onSelect={handleMoveToDate} />}
          hideTrigger={['clickoutside', 'esckey']}
          showTrigger={[]}
          open={dateOpen}
          position="top"
        >
          <div ref={shiftBtnRef}>
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.TERTIARY}
              leftIcon={Calendar}
              onClick={() => setDateOpen((prev) => !prev)}
            >
              Move Date
            </Button>
          </div>
        </Dialog>

        {/* ── Shift duration ───────────────────────────────────────────── */}
        <Dialog
          content={<ShiftDurationContent onSelect={handleShift} />}
          hideTrigger={['clickoutside', 'esckey']}
          showTrigger={[]}
          open={shiftOpen}
          position="top"
        >
          <Button
            size={Button.sizes.SMALL}
            kind={Button.kinds.TERTIARY}
            leftIcon={MoveArrowRight}
            onClick={() => setShiftOpen((prev) => !prev)}
          >
            Shift Duration
          </Button>
        </Dialog>

        {/* ── Spacer ───────────────────────────────────────────────────── */}
        <div className="rp-ms-toolbar__spacer" />

        {/* ── Cancel selection ─────────────────────────────────────────── */}
        <IconButton
          icon={Close}
          size={IconButton.sizes.SMALL}
          kind={IconButton.kinds.TERTIARY}
          onClick={onCancelSelection}
          ariaLabel="Cancel selection"
        />
      </Flex>
    </div>
  );
};

export default MultiSelectToolbar;
